/**
 * Gráfico (item CHART): modelo, validação e desenho em SVG com o ECharts.
 *
 * Arquivo CANÔNICO, copiado VERBATIM para a API (src/modules/document/utils/chart-render.ts).
 * A API desenha o SVG que fica gravado no item e vai para o PDF (o PdfEngine só cola o SVG,
 * e o app mostra o SVG compacto). A web desenha o MESMO SVG na hora, enquanto o usuário
 * edita. Os dois lados usam o ECharts 6.1.0 exato, esta mesma função e a mesma medida de
 * texto (`chartMeasureText`), então o SVG sai igual byte a byte.
 *
 * Puro: sem imports de Angular nem de Node. O ECharts chega por parâmetro (a web passa o
 * build modular, a API o build completo).
 */

// #region Modelo

export const CHART_KINDS = ['column', 'bar', 'stacked', 'combo', 'line', 'area', 'pie', 'donut'] as const;
export type ChartKind = (typeof CHART_KINDS)[number];

/** Paletas de cor, da primeira série para a última. As famílias são as do Destaque. */
export const CHART_PALETTES = {
    default: ['#2a89ef', '#15be78', '#ff850a', '#e04f4f', '#6366f1', '#9ca3af'],
    highlight: ['#9ca3af', '#ff850a', '#2a89ef', '#15be78', '#6366f1', '#4b5563'],
    blue: ['#1d5fb8', '#2a89ef', '#7fb3f5', '#b9d3f7', '#4b5563', '#9ca3af'],
    green: ['#1e7a46', '#15be78', '#7fd8b0', '#b4e6cf', '#4b5563', '#9ca3af'],
    warm: ['#b4561a', '#ff850a', '#f9ab10', '#e04f4f', '#b42323', '#9ca3af'],
    sober: ['#374151', '#6b7280', '#9ca3af', '#d1d5db', '#2a89ef', '#e04f4f'],
} as const;
export type ChartPalette = keyof typeof CHART_PALETTES;
export const CHART_PALETTE_KEYS = Object.keys(CHART_PALETTES) as ChartPalette[];

export const CHART_LEGENDS = ['top', 'bottom', 'none'] as const;
export type ChartLegend = (typeof CHART_LEGENDS)[number];

export const CHART_UNITS = ['number', 'currency', 'percent'] as const;
export type ChartUnit = (typeof CHART_UNITS)[number];

export const CHART_HEIGHTS = ['low', 'medium', 'high'] as const;
export type ChartHeight = (typeof CHART_HEIGHTS)[number];

export interface ChartStyle {
    palette: ChartPalette;
    legend: ChartLegend;
    /** Número em cima de cada barra/ponto; na pizza e na rosca, o percentual de cada fatia. */
    values: boolean;
    unit: ChartUnit;
    gridLines: boolean;
    /** Só linhas, área e a linha do "colunas e linha". */
    smooth: boolean;
    height: ChartHeight;
}

/**
 * Conteúdo do item CHART. A tabela é a mesma ideia do item TABLE: a 1ª coluna são as
 * categorias (meses, etapas, equipes) e cada coluna a mais é uma série de números. As
 * células ficam como o usuário digitou ("1.234,56"); o número é lido na hora de desenhar.
 */
export interface ChartValue {
    kind: ChartKind;
    title: string;
    columns: string[];
    rows: string[][];
    style: ChartStyle;
    /** SVG para o PDF e a web. Gerado por `renderChartSvg`; o cliente nunca manda. */
    svg?: string;
    /** SVG estreito para o card do app. Gerado por `renderChartSvg(..., { compact: true })`. */
    svgCompact?: string;
}

export const CHART_LIMITS = {
    /** Categorias + pelo menos uma série. */
    MIN_COLUMNS: 2,
    /** Categorias + seis séries: mais que isso não se lê numa folha A4. */
    MAX_COLUMNS: 7,
    MAX_ROWS: 50,
    MAX_TITLE_LENGTH: 120,
    MAX_HEADER_LENGTH: 60,
    MAX_CELL_LENGTH: 40,
} as const;

export const DEFAULT_CHART_STYLE: ChartStyle = {
    palette: 'default',
    legend: 'top',
    values: true,
    unit: 'number',
    gridLines: true,
    smooth: true,
    height: 'medium',
};

/**
 * Tamanho do desenho em px CSS. A largura cheia é a coluna de texto da folha A4 do PDF
 * (o HTML ainda encaixa o SVG em 100% da coluna); a compacta é a do card do app.
 */
export const CHART_WIDTH = 720;
export const CHART_COMPACT_WIDTH = 360;
const CHART_HEIGHT_PX: Record<ChartHeight, number> = { low: 280, medium: 360, high: 460 };
const CHART_COMPACT_HEIGHT_PX: Record<ChartHeight, number> = { low: 200, medium: 240, high: 300 };

export function chartSize(height: ChartHeight, compact = false): { width: number; height: number } {
    return compact
        ? { width: CHART_COMPACT_WIDTH, height: CHART_COMPACT_HEIGHT_PX[height] ?? CHART_COMPACT_HEIGHT_PX.medium }
        : { width: CHART_WIDTH, height: CHART_HEIGHT_PX[height] ?? CHART_HEIGHT_PX.medium };
}

export function isPieKind(kind: ChartKind): boolean {
    return kind === 'pie' || kind === 'donut';
}

/** Tipos com linha, onde "linha suave" faz diferença. */
export function isLineKind(kind: ChartKind): boolean {
    return kind === 'line' || kind === 'area' || kind === 'combo';
}

/** Gráfico vazio (criado sem valor, pela IA ou por um cliente antigo): colunas, uma série. */
export function initialChartValue(): ChartValue {
    return {
        kind: 'column',
        title: '',
        columns: ['', ''],
        rows: [
            ['', ''],
            ['', ''],
            ['', ''],
        ],
        style: { ...DEFAULT_CHART_STYLE },
    };
}

// #endregion

// #region Validação

/**
 * Normaliza o valor que chega do cliente (ou da IA) para o formato gravado.
 *
 * - não é objeto com `columns` em lista => `null` (quem chama decide);
 * - tipo, paleta, legenda, unidade e altura fora da lista caem no padrão;
 * - colunas e células viram SEMPRE string aparada, numa linha só, cortada no teto;
 * - de 2 a 7 colunas (completa com coluna vazia, corta o excesso) e até 50 linhas, cada
 *   linha com exatamente uma célula por coluna;
 * - `svg` e `svgCompact` SOMEM: quem grava desenha de novo com `renderChartSvg`.
 */
export function sanitizeChartValue(raw: unknown): ChartValue | null {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const record = raw as Record<string, unknown>;
    if (!Array.isArray(record['columns'])) return null;

    const kind = oneOf(record['kind'], CHART_KINDS, 'column');
    const title = toText(record['title'], CHART_LIMITS.MAX_TITLE_LENGTH);

    const columns = (record['columns'] as unknown[])
        .slice(0, CHART_LIMITS.MAX_COLUMNS)
        .map(c => toText(c, CHART_LIMITS.MAX_HEADER_LENGTH));
    while (columns.length < CHART_LIMITS.MIN_COLUMNS) columns.push('');

    const rows: string[][] = [];
    for (const row of Array.isArray(record['rows']) ? (record['rows'] as unknown[]) : []) {
        if (rows.length >= CHART_LIMITS.MAX_ROWS) break;
        if (!Array.isArray(row)) continue;
        rows.push(columns.map((_, i) => toText(row[i], CHART_LIMITS.MAX_CELL_LENGTH)));
    }

    return { kind, title, columns, rows, style: sanitizeChartStyle(record['style']) };
}

export function sanitizeChartStyle(raw: unknown): ChartStyle {
    const s = raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
    const d = DEFAULT_CHART_STYLE;
    return {
        palette: oneOf(s['palette'], CHART_PALETTE_KEYS, d.palette),
        legend: oneOf(s['legend'], CHART_LEGENDS, d.legend),
        values: typeof s['values'] === 'boolean' ? s['values'] : d.values,
        unit: oneOf(s['unit'], CHART_UNITS, d.unit),
        gridLines: typeof s['gridLines'] === 'boolean' ? s['gridLines'] : d.gridLines,
        smooth: typeof s['smooth'] === 'boolean' ? s['smooth'] : d.smooth,
        height: oneOf(s['height'], CHART_HEIGHTS, d.height),
    };
}

function oneOf<T extends string>(raw: unknown, list: readonly T[], fallback: T): T {
    return typeof raw === 'string' && (list as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

function toText(raw: unknown, max: number): string {
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw).slice(0, max);
    if (typeof raw !== 'string') return '';
    return raw
        .replace(/\s*[\r\n\t]+\s*/g, ' ')
        .trim()
        .slice(0, max)
        .trim();
}

// #endregion

// #region Números

const CURRENCY_SYMBOLS: Record<string, string> = { BRL: 'R$', USD: '$', EUR: '€', GBP: '£' };

/**
 * Lê o número de uma célula do jeito que as pessoas digitam: "1.234,56", "1,234.56",
 * "R$ 1.234", "12%", "-3,5". Vazio ou texto => `null` (a célula não entra no gráfico).
 *
 * Ambiguidade resolvida a favor do português: "1.234" é mil duzentos e trinta e quatro e
 * "1,5" é um e meio. Mais de um separador igual é sempre milhar ("1.234.567").
 */
export function parseChartNumber(raw: string | null | undefined): number | null {
    let s = String(raw ?? '').replace(/[\s  ]/g, '');
    if (!s) return null;
    s = s.replace(/^(R\$|US\$|\$|€|£)/i, '').replace(/(R\$|€|£|%)$/i, '');
    if (!s) return null;

    const commas = (s.match(/,/g) ?? []).length;
    const dots = (s.match(/\./g) ?? []).length;
    if (commas > 0 && dots > 0) {
        s = s.lastIndexOf(',') > s.lastIndexOf('.') ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
    } else if (commas > 0) {
        s = commas > 1 ? s.replace(/,/g, '') : s.replace(',', '.');
    } else if (dots > 1 || /^-?\d{1,3}\.\d{3}$/.test(s)) {
        s = s.replace(/\./g, '');
    }

    if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

/** Separador de milhar e decimal pelo idioma (sem Intl: o SVG não pode variar com o ICU). */
function separators(locale: string | undefined): { group: string; decimal: string } {
    switch ((locale ?? 'pt-BR').slice(0, 2).toLowerCase()) {
        case 'en':
            return { group: ',', decimal: '.' };
        case 'fr':
            return { group: ' ', decimal: ',' };
        default:
            return { group: '.', decimal: ',' };
    }
}

function plainNumber(n: number, locale: string | undefined, maxDecimals: number): string {
    const { group, decimal } = separators(locale);
    const factor = Math.pow(10, maxDecimals);
    const rounded = Math.round((Math.abs(n) + Number.EPSILON) * factor) / factor;
    let [int, frac = ''] = rounded.toFixed(maxDecimals).split('.');
    frac = frac.replace(/0+$/, '');
    int = int.replace(/\B(?=(\d{3})+(?!\d))/g, group);
    return (n < 0 && rounded !== 0 ? '-' : '') + int + (frac ? decimal + frac : '');
}

function withUnit(text: string, unit: ChartUnit, locale: string | undefined, currencyCode: string | undefined): string {
    if (unit === 'percent') return text + '%';
    if (unit !== 'currency') return text;

    const symbol = CURRENCY_SYMBOLS[(currencyCode ?? '').toUpperCase()] ?? CURRENCY_SYMBOLS['BRL'];
    const lang = (locale ?? 'pt-BR').slice(0, 2).toLowerCase();
    if (lang === 'en') return symbol + text;
    if (lang === 'pt') return symbol + ' ' + text;
    return text + ' ' + symbol;
}

/** Número de um rótulo ("1.234,5", "R$ 1.234,5", "12,5%"). */
export function formatChartNumber(
    n: number,
    unit: ChartUnit,
    locale?: string,
    currencyCode?: string
): string {
    return withUnit(plainNumber(n, locale, 2), unit, locale, currencyCode);
}

/** Número do eixo: milhares e milhões abreviados ("12 mil", "1,2 mi"). */
function formatAxisNumber(n: number, unit: ChartUnit, locale?: string, currencyCode?: string): string {
    const lang = (locale ?? 'pt-BR').slice(0, 2).toLowerCase();
    const suffix: Record<string, [string, string]> = {
        pt: [' mil', ' mi'],
        es: [' mil', ' M'],
        de: [' Tsd.', ' Mio.'],
        fr: [' k', ' M'],
        en: ['k', 'M'],
    };
    const [thousand, million] = suffix[lang] ?? suffix['pt'];
    const abs = Math.abs(n);
    let text: string;
    if (abs >= 1e6) text = plainNumber(n / 1e6, locale, 1) + million;
    else if (abs >= 1e4) text = plainNumber(n / 1e3, locale, 1) + thousand;
    else text = plainNumber(n, locale, 2);
    return withUnit(text, unit, locale, currencyCode);
}

/** Valores numéricos que o desenho usa (na pizza e na rosca, só a 1ª série). */
export function chartNumericCount(value: Pick<ChartValue, 'kind' | 'columns' | 'rows'>): number {
    const last = isPieKind(value.kind) ? Math.min(2, value.columns.length) : value.columns.length;
    let count = 0;
    for (const row of value.rows) {
        for (let c = 1; c < last; c++) if (parseChartNumber(row[c]) !== null) count++;
    }
    return count;
}

// #endregion

// #region Medida de texto

/*
 * Largura de cada caractere (em fração do tamanho da fonte) para o ECharts posicionar
 * rótulos e legenda. Sem isto a API (Node, sem canvas) usaria uma tabela e o navegador o
 * canvas, e o SVG sairia diferente nos dois. É a tabela padrão do zrender (ASCII 32-126);
 * letra acentuada mede como a letra sem acento.
 */
const WIDTH_MAP_STR =
    "007LLmW'55;N0500LLLLLLLLLL00NNNLzWW\\\\WQb\\0FWLg\\bWb\\WQ\\WrWWQ000CL5LLFLL0LL**F*gLLLL5F0LF\\FFF5.5N";
const WIDTH_MAP: Record<string, number> = {};
for (let i = 0; i < WIDTH_MAP_STR.length; i++) {
    WIDTH_MAP[String.fromCharCode(i + 32)] = (WIDTH_MAP_STR.charCodeAt(i) - 20) / 100;
}

export function chartMeasureText(text: string, font?: string): { width: number } {
    const match = /((?:\d+)?\.?\d*)px/.exec(font ?? '');
    const size = (match && +match[1]) || 12;
    const bold = /\b(bold|[6-9]00)\b/.test(font ?? '');
    let width = 0;
    for (const ch of text ?? '') {
        const base = WIDTH_MAP[ch] ?? WIDTH_MAP[ch.normalize('NFD')[0]];
        width += base == null ? size : base * size;
    }
    return { width: bold ? width * 1.05 : width };
}

// #endregion

// #region Desenho

/** O que este arquivo usa do ECharts (a web passa o modular, a API o completo). */
export interface ChartEcharts {
    init(
        dom: null,
        theme: null,
        opts: { renderer: 'svg'; ssr: true; width: number; height: number }
    ): { setOption(option: object): void; renderToSVGString(): string; dispose(): void };
    setPlatformAPI(api: { measureText: (text: string, font?: string) => { width: number } }): void;
}

export interface ChartRenderOptions {
    /** Idioma do documento (separadores e abreviações). Padrão pt-BR. */
    locale?: string;
    /** Moeda do documento (BRL, USD, EUR, GBP) para a unidade "moeda". Padrão BRL. */
    currencyCode?: string;
    /** Versão estreita do card do app. */
    compact?: boolean;
}

const FONT = 'Roboto, Arial, sans-serif';

/**
 * Desenha o gráfico e devolve o SVG (texto). Sem nenhum número para desenhar => ''.
 *
 * O SVG sai limpo: sem o <style> de hover do ECharts e sem as classes numeradas por
 * instância (que mudariam de um desenho para o outro). Ver `cleanSvg`.
 */
export function renderChartSvg(echarts: ChartEcharts, value: ChartValue, options: ChartRenderOptions = {}): string {
    if (chartNumericCount(value) === 0) return '';

    echarts.setPlatformAPI({ measureText: chartMeasureText });
    const { width, height } = chartSize(value.style.height, options.compact);
    const chart = echarts.init(null, null, { renderer: 'svg', ssr: true, width, height });
    try {
        chart.setOption(buildChartOption(value, width, height, options));
        return cleanSvg(chart.renderToSVGString());
    } finally {
        chart.dispose();
    }
}

/**
 * Tira o que muda de um desenho para o outro sem mudar o desenho:
 *
 * - o <style> de hover e as classes `zrN-cls-M` (o SVG é estático);
 * - o prefixo `zrN-` dos ids de recorte e gradiente, que é um contador da instância. Ele
 *   vira um hash do próprio SVG: o mesmo gráfico sai sempre igual, e dois gráficos
 *   diferentes na mesma página não disputam o mesmo id (id de SVG embutido é global no
 *   documento HTML).
 */
function cleanSvg(svg: string): string {
    const normalized = svg
        .replace(/<style[^>]*>[\s\S]*?<\/style>/g, '')
        .replace(/ class="zr\d+-cls-\d+"/g, '')
        .replace(/\bzr\d+-/g, 'zr0-')
        .replace(/\n{2,}/g, '\n')
        .trim();
    return normalized.replace(/\bzr0-/g, `ch${fnv1a(normalized)}-`);
}

/** FNV-1a de 32 bits em base 36: curto, estável e igual no navegador e no Node. */
function fnv1a(text: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(36);
}

interface Sizes {
    base: number;
    axis: number;
    title: number;
    label: number;
    legend: number;
    titleH: number;
    legendH: number;
    legendGap: number;
    box: number;
    pieSide: number;
}

const FULL: Sizes = { base: 13, axis: 12, title: 16, label: 11, legend: 13, titleH: 34, legendH: 30, legendGap: 18, box: 12, pieSide: 160 };
const COMPACT: Sizes = { base: 11, axis: 10, title: 13, label: 10, legend: 11, titleH: 24, legendH: 24, legendGap: 12, box: 10, pieSide: 84 };

/** Opção do ECharts para o valor. Exportada para os testes. */
export function buildChartOption(
    value: ChartValue,
    width: number,
    height: number,
    options: ChartRenderOptions = {}
): Record<string, unknown> {
    const t = options.compact ? COMPACT : FULL;
    const { locale, currencyCode } = options;
    const style = value.style;
    const colors = [...(CHART_PALETTES[style.palette] ?? CHART_PALETTES.default)];
    const kind = value.kind;
    const pie = isPieKind(kind);
    const unit = style.unit;
    const fmt = (n: number) => formatChartNumber(n, unit, locale, currencyCode);

    const categories = value.rows.map(r => r[0] ?? '');
    const names = value.columns.slice(1).map((c, i) => c || `${i + 1}`);
    const series = names.map((_, j) => value.rows.map(r => parseChartNumber(r[j + 1])));

    const hasTitle = !!value.title.trim();
    const legendCount = pie ? categories.length : names.length;
    const legendOn = style.legend !== 'none' && legendCount > (pie ? 0 : 1);
    const legendTop = legendOn && style.legend === 'top';
    const top = (hasTitle ? t.titleH : 6) + (legendTop ? t.legendH : 0);
    const bottom = legendOn && !legendTop ? t.legendH + 4 : 4;

    const option: Record<string, unknown> = {
        animation: false,
        color: colors,
        textStyle: { fontFamily: FONT, fontSize: t.base, color: '#4b5563' },
    };
    if (hasTitle) {
        option['title'] = {
            text: value.title,
            left: 0,
            top: 0,
            textStyle: { fontFamily: FONT, fontSize: t.title, fontWeight: 600, color: '#141515' },
        };
    }
    if (legendOn) {
        const legend: Record<string, unknown> = {
            left: 0,
            icon: 'roundRect',
            itemWidth: t.box,
            itemHeight: t.box,
            itemGap: t.legendGap,
            textStyle: { fontFamily: FONT, fontSize: t.legend, color: '#4b5563' },
        };
        if (legendTop) legend['top'] = hasTitle ? t.titleH - 2 : 0;
        else legend['bottom'] = 0;
        option['legend'] = legend;
    } else {
        option['legend'] = { show: false };
    }

    if (pie) {
        const data = value.rows
            .map(r => ({ name: r[0] ?? '', value: parseChartNumber(r[1]) }))
            .filter((d): d is { name: string; value: number } => d.value !== null && d.value > 0);
        const total = data.reduce((sum, d) => sum + d.value, 0);
        const area = height - top - bottom;
        const cy = top + area / 2;
        const radius = Math.max(32, Math.min(area / 2 - 12, width / 2 - t.pieSide));

        option['series'] = [
            {
                type: 'pie',
                silent: true,
                center: [width / 2, cy],
                radius: kind === 'donut' ? [radius * 0.58, radius] : [0, radius],
                data,
                startAngle: 90,
                avoidLabelOverlap: true,
                itemStyle: { borderColor: '#ffffff', borderWidth: 2 },
                label: {
                    show: true,
                    color: '#374151',
                    fontFamily: FONT,
                    fontSize: t.label + 1,
                    lineHeight: t.label + 5,
                    formatter: (p: { name: string; percent: number }) =>
                        style.values ? `${p.name}\n${plainNumber(p.percent, locale, 1)}%` : p.name,
                },
                labelLine: {
                    show: true,
                    length: options.compact ? 6 : 10,
                    length2: options.compact ? 8 : 14,
                    lineStyle: { color: '#d1d5db' },
                },
            },
        ];

        if (kind === 'donut' && total > 0) {
            const big = options.compact ? 15 : 20;
            const small = options.compact ? 10 : 12;
            const centered = { align: 'center', verticalAlign: 'middle', textAlign: 'center', textVerticalAlign: 'middle' };
            option['graphic'] = [
                {
                    type: 'text',
                    silent: true,
                    x: width / 2,
                    y: cy - (options.compact ? 6 : 9),
                    style: { text: fmt(total), fill: '#141515', font: `700 ${big}px ${FONT}`, ...centered },
                },
                {
                    type: 'text',
                    silent: true,
                    x: width / 2,
                    y: cy + (options.compact ? 10 : 14),
                    style: { text: 'Total', fill: '#6b7280', font: `400 ${small}px ${FONT}`, ...centered },
                },
            ];
        }
        return option;
    }

    const horizontal = kind === 'bar';
    const showValues = style.values;
    const categoryAxis: Record<string, unknown> = {
        type: 'category',
        data: categories,
        axisTick: { show: false },
        axisLine: { show: true, lineStyle: { color: '#d1d5db' } },
        axisLabel: { color: '#4b5563', fontFamily: FONT, fontSize: t.axis },
        boundaryGap: !(kind === 'line' || kind === 'area'),
    };
    const valueAxis: Record<string, unknown> = {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            color: '#6b7280',
            fontFamily: FONT,
            fontSize: t.axis,
            formatter: (n: number) => formatAxisNumber(n, unit, locale, currencyCode),
        },
        splitLine: { show: style.gridLines, lineStyle: { color: '#e5e7eb' } },
    };
    if (horizontal) {
        categoryAxis['inverse'] = true;
        option['xAxis'] = valueAxis;
        option['yAxis'] = categoryAxis;
    } else {
        option['xAxis'] = categoryAxis;
        option['yAxis'] = valueAxis;
    }
    option['grid'] = {
        left: 2,
        right: horizontal && showValues ? (options.compact ? 44 : 72) : options.compact ? 6 : 12,
        top: top + (options.compact ? 6 : 8),
        bottom: bottom + 2,
        outerBoundsMode: 'same',
        outerBoundsContain: 'axisLabel',
    };

    const label = (position: string, color = '#374151') => ({
        show: showValues,
        position,
        color,
        fontFamily: FONT,
        fontSize: t.label,
        formatter: (p: { value: number | null }) => (p.value === null || p.value === undefined ? '' : fmt(p.value)),
    });

    option['series'] = names.map((name, j) => {
        const asLine = kind === 'line' || kind === 'area' || (kind === 'combo' && j > 0);
        if (!asLine) {
            const stacked = kind === 'stacked';
            const bar: Record<string, unknown> = {
                type: 'bar',
                silent: true,
                name,
                data: series[j],
                barMaxWidth: horizontal ? (options.compact ? 14 : 22) : options.compact ? 18 : 34,
                barGap: '18%',
                barCategoryGap: '38%',
                itemStyle: { borderRadius: stacked ? 0 : horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] },
                label: stacked ? label('inside', '#ffffff') : label(horizontal ? 'right' : 'top'),
                // Rótulo que cairia em cima de outro some (colunas vizinhas de séries diferentes).
                labelLayout: { hideOverlap: true },
            };
            if (stacked) bar['stack'] = 'total';
            return bar;
        }

        const line: Record<string, unknown> = {
            type: 'line',
            silent: true,
            name,
            data: series[j],
            smooth: style.smooth,
            symbol: 'circle',
            symbolSize: options.compact ? 5 : 7,
            lineStyle: { width: options.compact ? 2 : 2.5 },
            label: label('top'),
            labelLayout: { hideOverlap: true },
            connectNulls: true,
            z: 3,
        };
        if (kind === 'area') line['areaStyle'] = { opacity: 0.16 };
        return line;
    });

    return option;
}

// #endregion
